import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertCircle,
  Ban,
  CloudOff,
  Columns3,
  Crosshair,
  Eye,
  Gauge as GaugeIcon,
  RotateCcw,
  Rows3,
  ScanSearch,
  ShieldAlert,
  ShieldCheck,
  Timer,
  UploadCloud,
} from "lucide-react";
import Dropzone from "../components/Dropzone";
import Gauge from "../components/Gauge";
import HistogramProjection from "../components/HistogramProjection";
import Magnetic from "../components/Magnetic";
import PipelineConsole from "../components/PipelineConsole";
import OfflineModal from "../components/OfflineModal";
import TrySample from "../components/TrySample";
import SeverityScale from "../components/SeverityScale";
import { Reveal } from "../components/Reveal";
import { fetchSampleFile, findSample, frozenInjection, injectionStream, readError, sampleUrl } from "../lib/api";
import { useIsOffline } from "../lib/health";
import { bytes, count, seconds } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";
import usePipeline from "../hooks/usePipeline";

/** Stage ids match the progress events emitted by backend/detection/injection.py. */
const STAGES = [
  { id: "upload", name: "Upload document", note: "client" },
  { id: "render", name: "Draw the page", note: "200 dpi" },
  { id: "project", name: "Count the pixels", note: "rows · columns" },
  { id: "extract", name: "Check every letter", note: "against the page" },
  { id: "fingerprint", name: "Chunk and hash", note: "both readings" },
  { id: "classify", name: "Grade the severity", note: "4 bands" },
];

const REASON_COPY = {
  "background-fill": "coloured the same as the page",
  unpainted: "does not show up on the page",
  "zero-width": "zero-width Unicode character",
  "off-page": "placed outside the page",
};

const GUIDANCE = {
  none: {
    tone: "success",
    title: "No injection detected",
    body: "Every letter in this file shows up on the page, so there is nothing hidden in it.",
    action: "Any similarity tool will score this file correctly. You can still compare it here if you want the check done properly.",
  },
  low: {
    tone: "warn",
    title: "A few hidden characters",
    body: "A few letters are in the file but not on the page. They barely affect the score, so this might just be a quirk of whatever made the PDF.",
    action: "Have a look at the character table below, then open the document itself before you decide anything.",
  },
  medium: {
    tone: "warn",
    title: "A fair amount of hidden text",
    body: "There is a fair amount of text in this file that a reader would never see, mixed in with the visible words.",
    action: "Do not trust a similarity score from any ordinary tool on this file. Run the comparison here instead.",
  },
  high: {
    tone: "danger",
    title: "A lot of hidden text",
    body: "Most of what this file calls its text is not on the page. That is hard to do by accident.",
    action: "Throw out any similarity score you already have for this file, re-run the comparison here, and have someone look at it.",
  },
};

function Injection() {
  const prefersReducedMotion = useReducedMotion();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  //: True while the loaded file is the bundled sample. Gates the frozen result,
  //: so a document the user supplied is never given the sample's numbers.
  const [usingSample, setUsingSample] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const offline = useIsOffline();
  const controller = useRef(null);
  const pipeline = usePipeline(STAGES);
  const { running, start, handleEvent, succeed, fail, reset } = pipeline;

  useEffect(() => () => controller.current?.abort(), []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!file || running) return;

      setError(null);
      setResult(null);
      setFrozen(false);

      if (offline) {
        if (!usingSample) {
          setError({
            message:
              "The analysis service is offline, so your own document cannot be processed. The bundled sample still works.",
            canRetry: false,
          });
          return;
        }
        start("service offline, loading the captured sample result");
        try {
          const data = await frozenInjection();
          setFrozen(true);
          succeed("Captured from a real run of this same document");
          setResult(data);
        } catch (err) {
          const parsed = readError(err);
          setError(parsed);
          fail(parsed.message);
        }
        return;
      }

      controller.current = new AbortController();
      start(`POST /Character-Injection/stream  ${file.name}`);

      try {
        const data = await injectionStream({
          file,
          signal: controller.current.signal,
          onEvent: handleEvent,
        });
        succeed(
          `${data.is_injected ? "INJECTED" : "CLEAN"} · ${data.hidden_count} hidden · agreement ${data.ratio.toFixed(4)} · ${seconds(data.elapsed_seconds)}`
        );
        setResult(data);
      } catch (err) {
        const parsed = readError(err);
        setError(parsed);
        fail(parsed.message);
      }
    },
    [file, running, offline, usingSample, start, handleEvent, succeed, fail]
  );

  const cancel = useCallback(() => controller.current?.abort(), []);

  /** Drop the bundled padded document into the dropzone. */
  const loadSample = useCallback(([sample]) => {
    setResult(null);
    setFrozen(false);
    reset();
    setUsingSample(true);
    setFile(sample);
    setOfflineOpen(false);
  }, [reset]);

  const selectFile = useCallback((picked) => {
    setUsingSample(false);
    setFile(picked);
  }, []);

  const clearAll = useCallback(() => {
    controller.current?.abort();
    setFile(null);
    setResult(null);
    setError(null);
    setUsingSample(false);
    setFrozen(false);
    reset();
  }, [reset]);

  const severity = result ? (result.is_injected ? result.severity : "none") : null;
  const guidance = severity ? GUIDANCE[severity] ?? GUIDANCE.none : null;
  const transition = prefersReducedMotion ? { duration: 0 } : { duration: DUR.enter, ease: EASE_OUT };

  return (
    <div className="shell shell--wide work">
      <div className="work__head">
        <div>
          <span className="eyebrow">Single document</span>
          <h1 className="work__title" style={{ marginTop: "var(--space-3)" }}>
            Character injection test
          </h1>
          <p className="work__lede">
            Upload a PDF and DocCompare lists every letter in it that nobody can see: which letter,
            which page, how big it is, what colour it was given. It finds them by comparing what the
            file claims its text is against what the page actually shows.
          </p>
        </div>
        <span className="badge badge--accent">
          <ScanSearch size={11} aria-hidden="true" />
          PDF only
        </span>
      </div>

      <div className="work__grid">
        {/* ---------------- INPUT RAIL ---------------- */}
        <form className="work__rail" onSubmit={submit} noValidate>
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">
                <UploadCloud size={13} aria-hidden="true" />
                Input
              </span>
              {file && (
                <button type="button" className="btn btn--quiet btn--sm" onClick={clearAll}>
                  <RotateCcw size={12} aria-hidden="true" />
                  Reset
                </button>
              )}
            </div>
            <div className="panel__body stack stack-4">
              {!file && (
                <>
                  <TrySample
                    need={1}
                    onLoaded={loadSample}
                    onError={setError}
                    disabled={running}
                  />
                  <span className="trysample-divider">or upload your own</span>
                </>
              )}
              <Dropzone
                label="PDF document"
                accept=".pdf"
                hint="PDF only. A text file has no rendered page to compare against."
                file={file}
                onSelect={selectFile}
                disabled={running}
                required
                blocked={offline}
                onBlockedClick={() => setOfflineOpen(true)}
                downloadUrl={usingSample && file ? sampleUrl(file.name) : undefined}
              />
            </div>
            <div className="panel__foot runbar">
              <Magnetic strength={0.16}>
                <button
                  type="submit"
                  className="btn btn--primary btn--block btn--lg"
                  disabled={!file || running}
                >
                  <span className="btn__sheen" aria-hidden="true" />
                  {running ? (
                    <>
                      <span className="stage__spin" aria-hidden="true" />
                      Scanning… {seconds(pipeline.elapsed)}
                    </>
                  ) : (
                    <>
                      <Eye size={16} aria-hidden="true" />
                      Scan for injection
                    </>
                  )}
                </button>
              </Magnetic>

              {running && (
                <button type="button" className="btn btn--danger btn--block btn--sm" onClick={cancel}>
                  <Ban size={12} aria-hidden="true" />
                  Cancel scan
                </button>
              )}

              <p className="runbar__meta">
                <span>Threshold {result?.threshold?.toFixed(2) ?? "0.98"}</span>
                <span>{file ? "1 file queued" : "1 file required"}</span>
              </p>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                className="alert alert--danger"
                role="alert"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={transition}
              >
                <AlertCircle size={16} className="alert__icon" aria-hidden="true" />
                <div className="alert__body stack stack-2">
                  <span className="alert__title">Scan failed</span>
                  <p>{error.message}</p>
                  {error.canRetry && (
                    <button type="submit" className="btn btn--ghost btn--sm" disabled={!file}>
                      <RotateCcw size={12} aria-hidden="true" />
                      Try again
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* ---------------- CONSOLE + VERDICT ---------------- */}
        <div className="work__main">
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">
                <Crosshair size={13} aria-hidden="true" />
                Pipeline
              </span>
              <span className="badge">
                {pipeline.status === "idle"
                  ? "Waiting"
                  : pipeline.status === "done"
                  ? "Complete"
                  : pipeline.status === "failed"
                  ? "Failed"
                  : seconds(pipeline.elapsed)}
              </span>
            </div>
            <div className="panel__body">
              <PipelineConsole
                stages={STAGES}
                stageStates={pipeline.stageStates}
                stageDetails={pipeline.stageDetails}
                log={pipeline.log}
                elapsed={pipeline.elapsed}
                status={pipeline.status}
              />
            </div>
          </div>

          <AnimatePresence>
            {result && (
              <motion.div
                className="results"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: DUR.reveal, ease: EASE_OUT }}
              >
                {frozen && (
                  <div className="alert alert--warn">
                    <CloudOff size={16} className="alert__icon" aria-hidden="true" />
                    <div className="alert__body">
                      <span className="alert__title">Captured result</span>
                      <p>
                        The service is offline, so these are the real numbers from an earlier run of
                        this same sample rather than a live scan. Start the service to check your own
                        documents.
                      </p>
                    </div>
                  </div>
                )}

                <div className="results__split">
                  <div className="verdict" data-state={result.is_injected ? "injected" : "clean"}>
                    <span className="caps dim">Verdict</span>
                    <motion.span
                      className="verdict__stamp"
                      initial={prefersReducedMotion ? false : { scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={
                        prefersReducedMotion
                          ? { duration: 0 }
                          : { type: "spring", stiffness: 260, damping: 16, delay: 0.08 }
                      }
                    >
                      {result.is_injected ? (
                        <ShieldAlert size={30} aria-hidden="true" />
                      ) : (
                        <ShieldCheck size={30} aria-hidden="true" />
                      )}
                      {result.is_injected ? "INJECTED" : "CLEAN"}
                    </motion.span>

                    {result.is_injected && (
                      <span
                        className={`badge ${
                          result.severity === "high" ? "badge--danger" : "badge--warn"
                        }`}
                      >
                        {result.severity} severity
                      </span>
                    )}

                    <p className="verdict__sub">
                      {result.is_injected ? (
                        <>
                          <strong className="num">{count(result.hidden_count)}</strong> of the{" "}
                          {count(result.raw_char_count)} letters in this file never show up on the
                          page
                          {result.injected_chars?.length > 0 && (
                            <>, and they are all ‘{result.injected_chars.slice(0, 3).join("’, ‘")}’</>
                          )}
                          .
                        </>
                      ) : (
                        <>
                          All {count(result.visible_char_count)} letters in this file show up on the
                          page. Nothing is hidden here.
                        </>
                      )}
                    </p>
                  </div>

                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <GaugeIcon size={13} aria-hidden="true" />
                        Fingerprint agreement
                      </span>
                      <span className="badge">
                        <Timer size={11} aria-hidden="true" />
                        {seconds(result.elapsed_seconds)}
                      </span>
                    </div>
                    <div className="panel__body stack stack-5">
                      <div style={{ display: "grid", placeItems: "center" }}>
                        <Gauge
                          value={result.ratio}
                          label="Extracted ↔ visible"
                          size={140}
                          stroke={11}
                          color={result.is_injected ? "var(--rose-500)" : "var(--accent)"}
                        />
                      </div>
                      <dl className="kv" style={{ margin: 0 }}>
                        <dt className="kv__k">File</dt>
                        <dd className="kv__v" style={{ margin: 0 }}>
                          {result.file_name}
                        </dd>
                        <dt className="kv__k">Size</dt>
                        <dd className="kv__v mono" style={{ margin: 0 }}>
                          {bytes(result.file_size_bytes)} · {result.page_count}p
                        </dd>
                        <dt className="kv__k">Characters</dt>
                        <dd className="kv__v mono" style={{ margin: 0 }}>
                          {count(result.visible_char_count)} visible / {count(result.raw_char_count)}{" "}
                          extracted
                        </dd>
                        <dt className="kv__k">Agreement</dt>
                        <dd className="kv__v mono" style={{ margin: 0 }}>
                          {result.ratio.toFixed(4)} (threshold {result.threshold?.toFixed(2) ?? "0.98"})
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>

                <div className="panel">
                  <div className="panel__head">
                    <span className="panel__title">
                      <ScanSearch size={13} aria-hidden="true" />
                      Severity bands
                    </span>
                  </div>
                  <div className="panel__body">
                    <SeverityScale ratio={result.ratio} threshold={result.threshold ?? 0.98} />
                  </div>
                </div>

                {result.hidden_samples?.length > 0 && (
                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <ShieldAlert size={13} aria-hidden="true" />
                        Hidden characters found
                      </span>
                      <span className="badge badge--danger">
                        {count(result.hidden_count)} total
                        {result.hidden_samples.length < result.hidden_count &&
                          ` · first ${result.hidden_samples.length}`}
                      </span>
                    </div>
                    <div className="panel__body stack stack-4">
                      <div className="samples__scroll">
                        <table className="samples">
                          <caption className="sr-only">
                            Letters that are in the file but do not show up on the page
                          </caption>
                          <thead>
                            <tr>
                              <th scope="col">Char</th>
                              <th scope="col">Codepoint</th>
                              <th scope="col">Page</th>
                              <th scope="col">Size</th>
                              <th scope="col">Fill</th>
                              <th scope="col">Why it is hidden</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.hidden_samples.map((sample, index) => (
                              <tr key={`${sample.codepoint}-${index}`}>
                                <td>
                                  <span className="samples__char">{sample.char.trim() || "␠"}</span>
                                </td>
                                <td>{sample.codepoint}</td>
                                <td>{sample.page + 1}</td>
                                <td>{sample.font_size ? `${sample.font_size}pt` : "—"}</td>
                                <td>
                                  {sample.color ? (
                                    <>
                                      <span
                                        className="samples__swatch"
                                        style={{ background: sample.color }}
                                        aria-hidden="true"
                                      />
                                      {sample.color}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>{REASON_COPY[sample.reason] || sample.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {Object.keys(result.reasons || {}).length > 0 && (
                        <div className="chip-set">
                          {Object.entries(result.reasons).map(([reason, total]) => (
                            <span className="chip" key={reason}>
                              {REASON_COPY[reason] || reason} · {total}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {result.projection && (
                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <Columns3 size={13} aria-hidden="true" />
                        Histogram projections · page 1
                      </span>
                      <span className="badge">HPH · VPH</span>
                    </div>
                    <div className="panel__body">
                      <HistogramProjection
                        projection={result.projection}
                        tone={result.is_injected ? "danger" : "accent"}
                        maxLines={4}
                      />
                    </div>
                  </div>
                )}

                {guidance && (
                  <div className={`alert alert--${guidance.tone}`}>
                    {result.is_injected ? (
                      <ShieldAlert size={16} className="alert__icon" aria-hidden="true" />
                    ) : (
                      <ShieldCheck size={16} className="alert__icon" aria-hidden="true" />
                    )}
                    <div className="alert__body stack stack-2">
                      <span className="alert__title">{guidance.title}</span>
                      <p>{guidance.body}</p>
                      <p>
                        <strong>What to do:</strong> {guidance.action}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {!result && !running && (
            <Reveal className="panel">
              <div className="panel__head">
                <span className="panel__title">
                  <Rows3 size={13} aria-hidden="true" />
                  How severity is graded
                </span>
              </div>
              <div className="panel__body stack stack-5">
                <p className="tile__body">
                  The bands show how much the hidden letters would skew a similarity score. A handful
                  barely moves it, while a document padded at every word drops to the bottom.
                </p>
                <SeverityScale ratio={null} threshold={0.98} />
                <div className="alert alert--info">
                  <AlertCircle size={16} className="alert__icon" aria-hidden="true" />
                  <div className="alert__body">
                    <p>
                      A hidden letter is not proof that someone meant to put it there. Templates and
                      generated PDFs sometimes carry white or off-page text of their own. Check the
                      table and the document itself before you conclude anything.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      <OfflineModal
        open={offlineOpen}
        onClose={() => setOfflineOpen(false)}
        onRunSample={async () => {
          const sample = findSample("injected");
          loadSample([await fetchSampleFile(sample)]);
        }}
      />
    </div>
  );
}

export default Injection;
